/**
 * 3D viewer: course from above at 45°. Camera position follows 2D canvas mouse (course mm).
 * Same colors as 2D canvas (shared colors.ts). Normal is straight up on the Z-axis (Z-up).
 */

import * as THREE from 'three';
import type { Course, Point, SurfaceKey } from './types';
import { SURFACE_KEYS } from './types';
import { polygonOffsetOutward } from './clipperOffset';
import {
  getLayerForSurfaceKey,
  getLayerForFeature,
  type LayerVisibility,
} from './layers';
import {
  SURFACE_COLORS,
  getSurfaceColor,
  TEE_BOX_FILL,
  TREE_COLOR,
  HOUSE_COLOR,
  getPinColor,
  GROUND_PLANE_COLOR,
  getTeePositionColor,
} from './colors';

/** Create a black-and-white barber-pole stripe texture for a cylinder (vertical stripes when wrapped). */
function createBarberPoleTexture(stripeCount: number): THREE.CanvasTexture {
  const w = 64;
  const h = Math.max(4, stripeCount * 4);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const c = canvas.getContext('2d');
  if (!c) return new THREE.CanvasTexture(canvas);
  const bandH = h / stripeCount;
  for (let i = 0; i < stripeCount; i++) {
    c.fillStyle = i % 2 === 0 ? '#ffffff' : '#1a1a1a';
    c.fillRect(0, i * bandH, w, bandH);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

/** Z layering: 1mm between each layer so overlapping 2D elements stack correctly in 3D.
 *  Base offset clears the elevation terrain (max ~12m). */
const Z_LAYER_MM = 1;
const Z_BASE_MM = 50;

/** Z-offset by draw order (matches 2D canvas: first drawn = back, last = on top). */
const SURFACE_Z_LAYERS: Record<SurfaceKey | 'fringe', number> = {
  secondCut: 1,
  firstCut: 2,
  fairway: 3,
  green: 5,
  fringe: 4,
  bunker: 6,
  waste: 7,
  water: 8,
  cartPath: 9,
  outOfBounds: 10,
  hazard: 11,
};

const FEATURE_Z_LAYERS = {
  tees: 12,
  trees: 13,
  houses: 14,
  pins: 15,
  teePositions: 16,
  markers150: 17,
} as const;

function zForSurface(key: SurfaceKey | 'fringe'): number {
  return Z_BASE_MM + SURFACE_Z_LAYERS[key]! * Z_LAYER_MM;
}

/** Build a flat mesh in the XY plane; normal = +Z (straight up). Course (x,y) → Three (x, y, z). */
function polygonToMesh(points: Point[], color: string, zOffsetMm: number): THREE.Mesh {
  const shape = new THREE.Shape();
  if (points.length >= 2) {
    const p0 = points[0];
    if (p0) shape.moveTo(p0[0], p0[1]);
    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      if (p) shape.lineTo(p[0], p[1]);
    }
    shape.closePath();
  }
  const geom = new THREE.ShapeGeometry(shape);
  const mat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.position.z = zOffsetMm;
  return mesh;
}

const CAMERA_ANGLE_DEG = 30; // look down at 30° from horizontal for depth
const CAMERA_HEIGHT_MIN_MM = 30000; // ~33 yd
const CAMERA_HEIGHT_MAX_MM = 150000; // ~164 yd
const CAMERA_HEIGHT_DEFAULT_MM = 85000; // ~93 yd (zoomed out from original 66 yd)
const YARD_TO_MM = 914.4;
const FRINGE_OFFSET_MM = 914.4; // 1 yard around green

export interface View3dOptions {
  onFlyoverChange?: (running: boolean) => void;
}

export function createView3d(
  container: HTMLElement,
  getCourse: () => Course,
  getCurrentHole: () => number,
  getLayerVisibility?: () => LayerVisibility,
  options?: View3dOptions
) {
  const onFlyoverChange = options?.onFlyoverChange;
  const layerVisible = (layerId: string | null) =>
    !getLayerVisibility || layerId === null || getLayerVisibility()[layerId as keyof LayerVisibility] !== false;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x1a1a1a);

  const camera = new THREE.PerspectiveCamera(50, 1, 1000, 2000000);
  camera.up.set(0, 0, 1);

  const PITCH_MIN = -Math.PI * 0.4;
  const PITCH_MAX = Math.PI * 0.4;

  let cameraTargetX = 0;
  let cameraTargetY = 0;
  let cameraHeightMm = CAMERA_HEIGHT_DEFAULT_MM;
  let orbitAngle = 0;
  let pitchAngle = 0;

  function updateCameraPosition() {
    const offsetY = cameraHeightMm / Math.tan((CAMERA_ANGLE_DEG * Math.PI) / 180);
    const dx = offsetY * Math.sin(orbitAngle);
    const dy = -offsetY * Math.cos(orbitAngle);
    const dz = cameraHeightMm;
    const camX = cameraTargetX + dx;
    const camY = cameraTargetY + dy * Math.cos(pitchAngle) - dz * Math.sin(pitchAngle);
    const camZ = dy * Math.sin(pitchAngle) + dz * Math.cos(pitchAngle);
    camera.position.set(camX, camY, camZ);
    camera.lookAt(cameraTargetX, cameraTargetY, 0);
  }
  updateCameraPosition();

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(window.devicePixelRatio ?? 1);
  container.appendChild(renderer.domElement);

  const overlay = document.createElement('div');
  overlay.className = 'view3d-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  container.appendChild(overlay);

  const courseGroup = new THREE.Group();
  scene.add(courseGroup);

  // Lighting so elevation relief is visible (sun from above and to the side)
  const ambient = new THREE.AmbientLight(0xffffff, 0.45);
  scene.add(ambient);
  const sun = new THREE.DirectionalLight(0xffffff, 0.85);
  sun.position.set(0.4, 0.3, 1).normalize();
  sun.target.position.set(0, 0, 0);
  scene.add(sun);
  scene.add(sun.target);

  const ELEVATION_SCALE_MM = 12000; // 0–1 grid value → mm height (~13 yd max)

  /** Build terrain mesh from elevation grid; vertices in course coords (x, y, z up). */
  function buildElevationTerrain(
    wMm: number,
    hMm: number,
    grid: { cols: number; rows: number; cellSizeMm: number; values: number[] }
  ): THREE.Mesh {
    const { cols, rows, cellSizeMm, values } = grid;
    const halfW = wMm / 2;
    const getZ = (ci: number, ri: number) =>
      values[Math.min(ri, rows - 1) * cols + Math.min(ci, cols - 1)]! * ELEVATION_SCALE_MM;

    const nx = cols + 1;
    const ny = rows + 1;
    const positions = new Float32Array(nx * ny * 3);
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const idx = (j * nx + i) * 3;
        positions[idx] = i * cellSizeMm - halfW;
        positions[idx + 1] = hMm - j * cellSizeMm;
        positions[idx + 2] = getZ(i, j);
      }
    }
    const indices: number[] = [];
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const a = j * nx + i;
        const b = a + 1;
        const c = a + nx;
        const d = c + 1;
        // CCW winding when viewed from +Z so front face (top of terrain) is lit correctly
        indices.push(a, c, d, a, d, b);
      }
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    const mat = new THREE.MeshLambertMaterial({
      color: GROUND_PLANE_COLOR,
      side: THREE.FrontSide,
    });
    return new THREE.Mesh(geom, mat);
  }

  function buildCourseMeshes() {
    const course = getCourse();
    const hole = getCurrentHole();
    const wMm = course.metadata.widthMm;
    const hMm = course.metadata.heightMm;

    courseGroup.clear();

    // Ground: elevation terrain (lit) or flat plane
    const grid = course.elevation?.grid;
    if (grid?.values?.length) {
      const terrain = buildElevationTerrain(wMm, hMm, grid);
      courseGroup.add(terrain);
    } else {
      const groundGeom = new THREE.PlaneGeometry(wMm, hMm);
      const groundMat = new THREE.MeshLambertMaterial({
        color: GROUND_PLANE_COLOR,
        side: THREE.DoubleSide,
      });
      const ground = new THREE.Mesh(groundGeom, groundMat);
      ground.position.set(0, hMm / 2, 0);
      courseGroup.add(ground);
    }

    // Surfaces (current hole only). Fringe = 1 yd around each green.
    // Each shape gets +0.1mm so overlapping shapes of same type stack (later-drawn on top).
    const fringeColor = SURFACE_COLORS.fringe;
    for (const key of SURFACE_KEYS) {
      if (key === 'fringe') continue;
      if (!layerVisible(getLayerForSurfaceKey(key))) continue;
      const color = getSurfaceColor(key);
      const shapes = course.surfaces[key];
      if (!shapes) continue;
      let shapeIndex = 0;
      for (const shape of shapes) {
        if (shape.holeNumber != null && shape.holeNumber !== hole) continue;
        if (shape.points.length < 3) continue;
        if (shape.type === 'path') continue;
        const subOffset = shapeIndex * 0.1;
        if (key === 'green') {
          const fringeRing = polygonOffsetOutward(shape.points, FRINGE_OFFSET_MM);
          if (fringeRing.length >= 3) {
            courseGroup.add(polygonToMesh(fringeRing, fringeColor, zForSurface('fringe') + subOffset));
          }
        }
        courseGroup.add(polygonToMesh(shape.points, color, zForSurface(key) + subOffset));
        shapeIndex++;
      }
    }

    // Tee boxes (current hole) — same color as 2D
    const teeZ = Z_BASE_MM + FEATURE_Z_LAYERS.tees * Z_LAYER_MM;
    if (layerVisible(getLayerForFeature('tees'))) {
      for (const tee of course.tees) {
        if (tee.holeNumber !== hole || tee.points.length < 3) continue;
        courseGroup.add(polygonToMesh(tee.points, TEE_BOX_FILL, teeZ));
      }
    }

    // Trees (current hole) — Hazards layer, same color as 2D
    const treeBaseZ = Z_BASE_MM + FEATURE_Z_LAYERS.trees * Z_LAYER_MM;
    if (layerVisible(getLayerForFeature('trees'))) {
      const treeGeom = new THREE.CylinderGeometry(1, 1.2, 1, 8);
      const treeMat = new THREE.MeshBasicMaterial({ color: TREE_COLOR });
      for (const tree of course.trees) {
        if ((tree.holeNumber ?? 1) !== hole) continue;
        const m = new THREE.Mesh(treeGeom, treeMat);
        m.scale.setScalar(tree.radiusMm);
        m.position.set(tree.x, tree.y, treeBaseZ + tree.radiusMm * 0.6);
        courseGroup.add(m);
      }
    }

    // Houses (current hole) — Hazards layer, rectangular base + triangular roof (full width and length)
    const houseBaseZ = Z_BASE_MM + FEATURE_Z_LAYERS.houses * Z_LAYER_MM;
    if (layerVisible(getLayerForFeature('houses'))) {
      const houseMat = new THREE.MeshBasicMaterial({ color: HOUSE_COLOR });
      const roofMat = new THREE.MeshBasicMaterial({ color: '#5a5a5a' });
      for (const house of course.houses) {
        if ((house.holeNumber ?? 1) !== hole) continue;
        const w = house.radiusMm * 1.8;
        const d = house.radiusMm * 1.2;
        const baseH = house.radiusMm * 0.5;
        const group = new THREE.Group();
        const baseGeom = new THREE.BoxGeometry(w, d, baseH);
        const base = new THREE.Mesh(baseGeom, houseMat);
        base.position.z = baseH / 2;
        group.add(base);
        const ridgeLen = Math.max(w, d);
        const span = Math.min(w, d);
        const r = span / Math.sqrt(3);
        const roofGeom = new THREE.CylinderGeometry(r, r, ridgeLen, 3);
        const roof = new THREE.Mesh(roofGeom, roofMat);
        if (w > d) roof.rotation.z = Math.PI / 2;
        roof.position.z = baseH + (r * Math.sqrt(3)) / 2;
        group.add(roof);
        group.position.set(house.x, house.y, houseBaseZ);
        courseGroup.add(group);
      }
    }

    // Pins — vertical pole 7 ft, flag 18 in tall × 2 ft long (Z-up: pole along Z, flag in XZ plane)
    const pinBaseZ = Z_BASE_MM + FEATURE_Z_LAYERS.pins * Z_LAYER_MM;
    if (layerVisible(getLayerForFeature('pins'))) {
      const INCH_TO_MM = 25.4;
      const poleHeight = 7 * 12 * INCH_TO_MM; // 7 ft
      const flagHeightMm = 18 * INCH_TO_MM; // 18 in tall
      const flagWidthMm = 2 * 12 * INCH_TO_MM; // 2 ft long
      const poleRadius = 50;
      const poleGeom = new THREE.CylinderGeometry(poleRadius, poleRadius, poleHeight, 8);
      const poleMat = new THREE.MeshBasicMaterial({ color: 0x2a2a2a });
      const flagGeom = new THREE.PlaneGeometry(flagWidthMm, flagHeightMm);
      for (const pin of course.pins) {
        if (pin.holeNumber !== hole) continue;
        const flagMat = new THREE.MeshBasicMaterial({ color: getPinColor(pin.day), side: THREE.DoubleSide });
        const group = new THREE.Group();
        const pole = new THREE.Mesh(poleGeom, poleMat);
        pole.rotation.x = Math.PI / 2; // cylinder axis Y -> Z (vertical)
        pole.position.set(0, 0, poleHeight / 2);
        group.add(pole);
        const flag = new THREE.Mesh(flagGeom, flagMat);
        flag.rotation.x = Math.PI / 2; // align with XZ plane (vertical flag)
        flag.position.set(flagWidthMm / 2, 0, poleHeight); // flag extends from pole top
        group.add(flag);
        group.position.set(pin.x, pin.y, pinBaseZ);
        courseGroup.add(group);
      }
    }

    // Tee positions — per-position color
    const teePosBaseZ = Z_BASE_MM + FEATURE_Z_LAYERS.teePositions * Z_LAYER_MM;
    if (layerVisible(getLayerForFeature('teePositions'))) {
      const teePosGeom = new THREE.SphereGeometry(1000, 8, 6);
      for (const pos of course.teePositions) {
        if (pos.holeNumber !== hole) continue;
        const mat = new THREE.MeshBasicMaterial({ color: getTeePositionColor(pos.color) });
        const m = new THREE.Mesh(teePosGeom, mat);
        m.position.set(pos.x, pos.y, teePosBaseZ + 1000);
        courseGroup.add(m);
      }
    }

    // 150 yd markers — black-and-white barber pole
    const m150BaseZ = Z_BASE_MM + FEATURE_Z_LAYERS.markers150 * Z_LAYER_MM;
    if (layerVisible(getLayerForFeature('markers150'))) {
      const pole150Radius = 500;
      const pole150Height = 6000;
      const stripeCount = 10;
      const barberTex = createBarberPoleTexture(stripeCount);
      const m150Geom = new THREE.CylinderGeometry(pole150Radius, pole150Radius, pole150Height, 16);
      const m150Mat = new THREE.MeshBasicMaterial({
        map: barberTex,
        side: THREE.DoubleSide,
      });
      for (const m of course.markers150) {
        if (m.holeNumber !== hole) continue;
        const mesh = new THREE.Mesh(m150Geom, m150Mat);
        mesh.position.set(m.x, m.y, m150BaseZ + pole150Height / 2);
        courseGroup.add(mesh);
      }
    }
  }

  function setCameraFromCoursePosition(xMm: number, yMm: number) {
    cameraTargetX = xMm;
    cameraTargetY = yMm;
    updateCameraPosition();
  }

  // Pan (left-drag), orbit (right-drag), zoom
  let isPanning = false;
  let isOrbiting = false;
  let grabbedWorldX = 0;
  let grabbedWorldY = 0;
  let lastOrbitX = 0;
  let lastOrbitY = 0;

  const zoomSensitivity = 1.15;
  const orbitSensitivity = 0.01;
  const pitchSensitivity = 0.01;
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const raycaster = new THREE.Raycaster();
  const mouseNDC = new THREE.Vector2();
  const intersectPoint = new THREE.Vector3();

  function screenToWorld(clientX: number, clientY: number): { x: number; y: number } | null {
    const rect = container.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    if (w <= 0 || h <= 0) return null;
    mouseNDC.x = ((clientX - rect.left) / w) * 2 - 1;
    mouseNDC.y = -((clientY - rect.top) / h) * 2 + 1;
    raycaster.setFromCamera(mouseNDC, camera);
    if (raycaster.ray.intersectPlane(groundPlane, intersectPoint)) {
      return { x: intersectPoint.x, y: intersectPoint.y };
    }
    return null;
  }

  function onDragMove(e: MouseEvent) {
    if (isPanning) {
      e.preventDefault();
      const world = screenToWorld(e.clientX, e.clientY);
      if (world) {
        cameraTargetX += grabbedWorldX - world.x;
        cameraTargetY += grabbedWorldY - world.y;
        updateCameraPosition();
      }
    } else if (isOrbiting) {
      e.preventDefault();
      const deltaX = e.clientX - lastOrbitX;
      const deltaY = e.clientY - lastOrbitY;
      orbitAngle -= deltaX * orbitSensitivity;
      pitchAngle = Math.max(PITCH_MIN, Math.min(PITCH_MAX, pitchAngle - deltaY * pitchSensitivity));
      lastOrbitX = e.clientX;
      lastOrbitY = e.clientY;
      updateCameraPosition();
    }
  }

  function onDragUp(e: MouseEvent) {
    if (e.button === 0) {
      isPanning = false;
      overlay.style.cursor = isOrbiting ? 'grabbing' : 'grab';
      overlay.style.userSelect = '';
      if (!isPanning && !isOrbiting) {
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', onDragUp);
      }
    } else if (e.button === 2) {
      isOrbiting = false;
      overlay.style.cursor = isPanning ? 'grabbing' : 'grab';
      if (!isPanning && !isOrbiting) {
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', onDragUp);
      }
    }
  }

  function on3dMouseDown(e: MouseEvent) {
    if (flyoverRafId) return;
    if (e.button === 0) {
      e.preventDefault();
      e.stopPropagation();
      if (returnTimeoutId) {
        clearTimeout(returnTimeoutId);
        returnTimeoutId = 0;
      }
      const world = screenToWorld(e.clientX, e.clientY);
      if (world) {
        isPanning = true;
        grabbedWorldX = world.x;
        grabbedWorldY = world.y;
        overlay.style.cursor = 'grabbing';
        overlay.style.userSelect = 'none';
        window.addEventListener('mousemove', onDragMove);
        window.addEventListener('mouseup', onDragUp);
      }
    } else if (e.button === 2) {
      e.preventDefault();
      e.stopPropagation();
      if (returnTimeoutId) {
        clearTimeout(returnTimeoutId);
        returnTimeoutId = 0;
      }
      isOrbiting = true;
      lastOrbitX = e.clientX;
      lastOrbitY = e.clientY;
      overlay.style.cursor = 'grabbing';
      overlay.style.userSelect = 'none';
      window.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup', onDragUp);
    }
  }

  overlay.addEventListener('contextmenu', (e: Event) => e.preventDefault());

  function on3dMouseLeave() {
    if (!isPanning && !isOrbiting) {
      overlay.style.cursor = 'grab';
      overlay.style.userSelect = '';
    }
  }

  function on3dTouchStart(e: TouchEvent) {
    if (flyoverRafId) return;
    if (e.touches.length === 1) {
      e.preventDefault();
      if (returnTimeoutId) {
        clearTimeout(returnTimeoutId);
        returnTimeoutId = 0;
      }
      const world = screenToWorld(e.touches[0]!.clientX, e.touches[0]!.clientY);
      if (world) {
        isPanning = true;
        grabbedWorldX = world.x;
        grabbedWorldY = world.y;
        container.addEventListener('touchmove', on3dTouchMove, { passive: false });
        container.addEventListener('touchend', on3dTouchEnd);
        container.addEventListener('touchcancel', on3dTouchEnd);
      }
    }
  }

  function on3dTouchMove(e: TouchEvent) {
    if (e.touches.length === 1 && isPanning) {
      e.preventDefault();
      const world = screenToWorld(e.touches[0]!.clientX, e.touches[0]!.clientY);
      if (world) {
        cameraTargetX += grabbedWorldX - world.x;
        cameraTargetY += grabbedWorldY - world.y;
        updateCameraPosition();
      }
    }
  }

  function on3dTouchEnd(e: TouchEvent) {
    if (e.touches.length === 0) {
      isPanning = false;
      container.removeEventListener('touchmove', on3dTouchMove);
      container.removeEventListener('touchend', on3dTouchEnd);
      container.removeEventListener('touchcancel', on3dTouchEnd);
    }
  }

  function on3dWheel(e: WheelEvent) {
    if (flyoverRafId) return;
    e.preventDefault();
    const factor = e.deltaY > 0 ? 1 / zoomSensitivity : zoomSensitivity;
    cameraHeightMm = Math.max(CAMERA_HEIGHT_MIN_MM, Math.min(CAMERA_HEIGHT_MAX_MM, cameraHeightMm * factor));
    updateCameraPosition();
  }

  overlay.addEventListener('mousedown', on3dMouseDown);
  overlay.addEventListener('mouseleave', on3dMouseLeave);
  overlay.addEventListener('touchstart', on3dTouchStart, { passive: false });
  overlay.addEventListener('wheel', on3dWheel, { passive: false });
  overlay.style.cursor = 'grab';

  function resize() {
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (width <= 0 || height <= 0) return;
    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  }

  let rafId = 0;
  function animate() {
    rafId = requestAnimationFrame(animate);
    renderer.render(scene, camera);
  }

  /** Centroid of a polygon (simple average of vertices). */
  function polygonCentroid(points: Point[]): [number, number] {
    let sx = 0, sy = 0;
    for (const p of points) {
      sx += p[0];
      sy += p[1];
    }
    return [sx / points.length, sy / points.length];
  }

  /** Tee box centroid closest to bottom (highest Y in top-left origin). */
  function getTeeClosestToBottom(hole: number): Point | null {
    const course = getCourse();
    let best: Point | null = null;
    let bestY = -Infinity;
    for (const tee of course.tees) {
      if (tee.holeNumber !== hole || !tee.points || tee.points.length < 3) continue;
      const [cx, cy] = polygonCentroid(tee.points);
      if (cy > bestY) {
        bestY = cy;
        best = [cx, cy];
      }
    }
    return best;
  }

  /** Green center for a hole (centroid of first green shape). */
  function getGreenCenter(hole: number): Point | null {
    const course = getCourse();
    const shapes = course.surfaces.green;
    if (!shapes) return null;
    for (const shape of shapes) {
      if (shape.holeNumber != null && shape.holeNumber !== hole) continue;
      if (!shape.points || shape.points.length < 3) continue;
      return polygonCentroid(shape.points);
    }
    return null;
  }

  let flyoverRafId = 0;
  let returnTimeoutId = 0;
  let flyoverSavedTargetX = 0;
  let flyoverSavedTargetY = 0;
  let flyoverSavedHeightMm = 0;
  let flyoverPrevUp: THREE.Vector3 | null = null;

  function startFlyover() {
    if (flyoverRafId) {
      cancelAnimationFrame(flyoverRafId);
      flyoverRafId = 0;
      if (returnTimeoutId) {
        clearTimeout(returnTimeoutId);
        returnTimeoutId = 0;
      }
      if (flyoverPrevUp) camera.up.copy(flyoverPrevUp);
      cameraTargetX = flyoverSavedTargetX;
      cameraTargetY = flyoverSavedTargetY;
      cameraHeightMm = flyoverSavedHeightMm;
      updateCameraPosition();
      onFlyoverChange?.(false);
      return;
    }
    if (returnTimeoutId) {
      clearTimeout(returnTimeoutId);
      returnTimeoutId = 0;
    }
    flyoverSavedTargetX = cameraTargetX;
    flyoverSavedTargetY = cameraTargetY;
    flyoverSavedHeightMm = cameraHeightMm;
    const course = getCourse();
    const hole = getCurrentHole();
    const wMm = course.metadata.widthMm;
    const hMm = course.metadata.heightMm;
    const teeCenter = getTeeClosestToBottom(hole);
    const greenCenter = getGreenCenter(hole);
    const gx = greenCenter ? greenCenter[0] : wMm / 2;
    const gy = greenCenter ? greenCenter[1] : hMm / 2;
    const tx = teeCenter ? teeCenter[0] : wMm / 2;
    const ty = teeCenter ? teeCenter[1] : 0;
    const dx = gx - tx;
    const dy = gy - ty;
    const approachLen = Math.hypot(dx, dy) || 1;
    const approachDx = dx / approachLen;
    const approachDy = dy / approachLen;
    const orbitRadiusMm = Math.max(30000, approachLen * 0.4);
    const theta0 = Math.atan2(approachDy, approachDx);
    const frontX = gx + orbitRadiusMm * Math.cos(theta0 + Math.PI);
    const frontY = gy + orbitRadiusMm * Math.sin(theta0 + Math.PI);
    const startBehindMm = 100 * YARD_TO_MM;
    const startX = tx - approachDx * startBehindMm;
    const startY = ty - approachDy * startBehindMm;
    const durationMs = 7000;
    const startTime = performance.now();

    cameraTargetX = tx;
    cameraTargetY = ty;
    orbitAngle = 0;
    pitchAngle = 0;
    camera.position.set(startX, startY, cameraHeightMm);
    camera.lookAt(tx, ty, 0);

    flyoverPrevUp = camera.up.clone();
    camera.up.set(0, 0, 1);
    onFlyoverChange?.(true);

    function tickFlyover() {
      flyoverRafId = requestAnimationFrame(tickFlyover);
      const elapsed = performance.now() - startTime;
      const tNorm = Math.min(1, elapsed / durationMs);
      const eased = tNorm < 0.5
        ? 2 * tNorm * tNorm
        : 1 - Math.pow(-2 * tNorm + 2, 2) / 2;

      let camX: number;
      let camY: number;
      let lookX: number;
      let lookY: number;
      if (eased < 0.5) {
        const t = eased / 0.5;
        camX = startX + (frontX - startX) * t;
        camY = startY + (frontY - startY) * t;
        const lookAheadMm = 100000;
        lookX = camX + approachDx * lookAheadMm;
        lookY = camY + approachDy * lookAheadMm;
      } else {
        const t = (eased - 0.5) / 0.5;
        const angle = theta0 + Math.PI * (1 - t);
        camX = gx + orbitRadiusMm * Math.cos(angle);
        camY = gy + orbitRadiusMm * Math.sin(angle);
        lookX = gx;
        lookY = gy;
      }

      // Smooth transition: blend look-at from approach direction to green over eased 0.35–0.65
      const blendStart = 0.35;
      const blendEnd = 0.65;
      if (eased > blendStart) {
        const lookAheadX = camX + approachDx * 100000;
        const lookAheadY = camY + approachDy * 100000;
        const blendT = Math.min(1, (eased - blendStart) / (blendEnd - blendStart));
        const smoothT = blendT * blendT * (3 - 2 * blendT); // smoothstep
        lookX = lookAheadX + (gx - lookAheadX) * smoothT;
        lookY = lookAheadY + (gy - lookAheadY) * smoothT;
      }

      camera.position.set(camX, camY, cameraHeightMm);
      camera.lookAt(lookX, lookY, 0);
      cameraTargetX = lookX;
      cameraTargetY = lookY;

      if (tNorm >= 1) {
        cancelAnimationFrame(flyoverRafId);
        flyoverRafId = 0;
        camera.up.copy(flyoverPrevUp!);
        onFlyoverChange?.(false);
        returnTimeoutId = window.setTimeout(() => {
          returnTimeoutId = 0;
          cameraTargetX = flyoverSavedTargetX;
          cameraTargetY = flyoverSavedTargetY;
          cameraHeightMm = flyoverSavedHeightMm;
          updateCameraPosition();
        }, 3000);
      }
    }
    tickFlyover();
  }

  resize();
  buildCourseMeshes();
  animate();
  window.addEventListener('resize', resize);

  function resetCamera() {
    cameraTargetX = 0;
    cameraTargetY = 0;
    cameraHeightMm = CAMERA_HEIGHT_DEFAULT_MM;
    orbitAngle = 0;
    pitchAngle = 0;
    updateCameraPosition();
  }

  return {
    setCameraFromCoursePosition,
    isUserPanning: () => isPanning || isOrbiting,
    isFlyoverRunning: () => flyoverRafId !== 0,
    startFlyover,
    resetCamera,
    updateCourse() {
      buildCourseMeshes();
    },
    dispose() {
      cancelAnimationFrame(rafId);
      if (flyoverRafId) cancelAnimationFrame(flyoverRafId);
      if (returnTimeoutId) clearTimeout(returnTimeoutId);
      window.removeEventListener('resize', resize);
      overlay.removeEventListener('mousedown', on3dMouseDown);
      overlay.removeEventListener('mouseleave', on3dMouseLeave);
      overlay.removeEventListener('touchstart', on3dTouchStart);
      overlay.removeEventListener('wheel', on3dWheel);
      if (isPanning || isOrbiting) {
        isPanning = false;
        isOrbiting = false;
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', onDragUp);
      }
      container.removeEventListener('touchmove', on3dTouchMove);
      container.removeEventListener('touchend', on3dTouchEnd);
      container.removeEventListener('touchcancel', on3dTouchEnd);
      renderer.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    },
  };
}

export type View3d = ReturnType<typeof createView3d>;
